import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CubeIcon, Squares2X2Icon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { useFeatures } from '../providers/FeaturesProvider';
import { API_URL } from '../runtimeConfig';
import {
  PageHeader,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  StatusBadge,
  Badge,
  EmptyState,
  ErrorAlert,
  Skeleton,
} from '../components/ui';

const SERVICE_FEATURE: Record<string, string> = {
  container: 'api-container',
  vps: 'api-vps',
  database: 'api-database',
  website: 'api-website',
  domain: 'api-domain',
  email: 'api-email',
  messaging: 'api-messaging',
  emailing: 'api-emailing',
  whatsapp: 'api-whatsapp',
};

interface Service {
  type: string;
  name: string;
  description?: string;
  status?: string;
  websiteId?: string;
  stackId?: string;
  vpsId?: number;
  emailId?: string;
  databaseId?: number;
}

const TYPE_LABEL: Record<string, string> = {
  vps: 'VPS',
  messaging: 'Messaging',
  emailing: 'Emailing',
  whatsapp: 'WhatsApp',
};

function prettyType(t: string) {
  if (!t) return '';
  return TYPE_LABEL[t] || t.charAt(0).toUpperCase() + t.slice(1);
}

export default function Services() {
  const [allServices, setAllServices] = useState<Service[]>([]);
  const { isEnabled } = useFeatures();
  const services = useMemo(
    () => allServices.filter((service) => isEnabled(SERVICE_FEATURE[service.type])),
    [allServices, isEnabled]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      const headers = { Authorization: `Bearer ${keycloak?.token}` };
      const [servicesRes, messagingRes, emailingRes, whatsappRes] = await Promise.all([
        fetch(`${API_URL}/services`, { headers }),
        fetch(`${API_URL}/messaging/token`, { headers }),
        fetch(`${API_URL}/emailing/token`, { headers }),
        fetch(`${API_URL}/whatsapp/token`, { headers }),
      ]);
      if (!servicesRes.ok) throw new Error('Failed to fetch services');
      const data: Service[] = await servicesRes.json();
      if (messagingRes.ok) {
        const tokenData = await messagingRes.json();
        if (tokenData?.credits > 0) {
          data.push({
            type: 'messaging',
            name: `Messages · ${tokenData.credits} credits`,
            description: `Messages · ${tokenData.credits} credits`,
            status: 'active',
          });
        }
      }
      if (emailingRes.ok) {
        const tokenData = await emailingRes.json();
        if (tokenData?.credits > 0) {
          data.push({
            type: 'emailing',
            name: `Emails · ${tokenData.credits} credits`,
            description: `Emails · ${tokenData.credits} credits`,
            status: 'active',
          });
        }
      }
      if (whatsappRes.ok) {
        const tokenData = await whatsappRes.json();
        if (tokenData?.credits > 0) {
          data.push({
            type: 'whatsapp',
            name: `WhatsApp · ${tokenData.credits} credits`,
            description: `WhatsApp · ${tokenData.credits} credits`,
            status: 'active',
          });
        }
      }
      setAllServices(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (service: Service) => {
    const type = (service.type || '').toLowerCase();
    if (type === 'domain') return navigate(`/domains?selected=${encodeURIComponent(service.name)}`);
    if (type === 'website' && service.websiteId) return navigate(`/websites/${service.websiteId}/files`);
    if (type === 'container' && service.stackId)
      return navigate(`/containers?stackId=${service.stackId}&mode=view`);
    if (type === 'vps' && service.vpsId) return navigate(`/vps?vpsId=${service.vpsId}`);
    if (type === 'email' && service.emailId) return navigate(`/emails?emailId=${service.emailId}`);
    if (type === 'database' && service.databaseId)
      return navigate(`/databases?databaseId=${service.databaseId}`);
    if (type === 'messaging') return navigate('/messages');
    if (type === 'emailing') return navigate('/emailing');
    if (type === 'whatsapp') return navigate('/whatsapp');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<CubeIcon />}
        title="Services"
        description="A unified list of your domains, websites, containers, VPS, email accounts, and databases."
      />

      {error && <ErrorAlert message={`Error: ${error}`} />}

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : services.length === 0 ? (
        <EmptyState
          icon={<Squares2X2Icon />}
          title="No services yet"
          description="Start by provisioning a domain, website, container, VPS, email account, or database."
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service, index) => (
                <TableRow
                  key={`${service.type}-${service.name}-${index}`}
                  onClick={() => handleRowClick(service)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <Badge variant="muted">{prettyType(service.type)}</Badge>
                  </TableCell>
                  <TableCell className="font-medium text-foreground">{service.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {service.description || '—'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={service.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <ChevronRightIcon className="h-4 w-4" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
